import React, { useState, useEffect } from 'react'
import './TSXScreener.css'
import FilterPanel from './FilterPanel'
import DataTable from './DataTable'
import ChartsPanel from './ChartsPanel'
import CompanyDetailModal from './CompanyDetailModal'
import dataService from '../services/dataService'
import { Database, TrendingUp, BarChart3, Filter, Search, BarChart, Settings, RefreshCw } from 'lucide-react'

const TSXScreener = () => {
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [filters, setFilters] = useState({
    sectors: [],
    industries: [],
    marketCapRange: [0, 1000000000000],
    peRange: [0, 100],
    dividendYieldRange: [0, 20],
    searchTerm: ''
  })
  const [sortConfig, setSortConfig] = useState({
    key: 'Market Cap',
    direction: 'desc'
  })

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [data, filters, sortConfig])

  const loadData = async () => {
    try {
      setLoading(true)
      const fetchedData = await dataService.getData()
      setData(fetchedData)
      setLastUpdated(new Date())
      setLoading(false)
    } catch (error) {
      console.error('Error loading data:', error)
      setLoading(false)
    }
  }

  const refreshData = async () => {
    try {
      setRefreshing(true)
      const freshData = await dataService.refreshData()
      setData(freshData)
      setLastUpdated(new Date())
      setRefreshing(false)
    } catch (error) {
      console.error('Error refreshing data:', error)
      setRefreshing(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...data]

    // Search filter
    if (filters.searchTerm) {
      filtered = filtered.filter(item => 
        item.Company?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        item.Ticker?.toLowerCase().includes(filters.searchTerm.toLowerCase())
      )
    }

    // Sector filter
    if (filters.sectors.length > 0) {
      filtered = filtered.filter(item => 
        item.Sector && filters.sectors.includes(item.Sector)
      )
    }

    // Industry filter
    if (filters.industries.length > 0) {
      filtered = filtered.filter(item => 
        item.Industry && filters.industries.includes(item.Industry)
      )
    }

    // Market Cap filter
    filtered = filtered.filter(item => 
      item['Market Cap'] && item['Market Cap'] >= filters.marketCapRange[0] && 
      item['Market Cap'] <= filters.marketCapRange[1]
    )

    // P/E filter
    filtered = filtered.filter(item => 
      !item['P/E'] || (item['P/E'] >= filters.peRange[0] && item['P/E'] <= filters.peRange[1])
    )

    // Dividend Yield filter
    filtered = filtered.filter(item => 
      !item['Dividend Yield'] || (item['Dividend Yield'] >= filters.dividendYieldRange[0] && item['Dividend Yield'] <= filters.dividendYieldRange[1])
    )

    // Sort data
    filtered.sort((a, b) => {
      const aVal = a[sortConfig.key]
      const bVal = b[sortConfig.key]
      
      if (aVal === null || aVal === undefined) return 1
      if (bVal === null || bVal === undefined) return -1
      
      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1
      } else {
        return aVal < bVal ? 1 : -1
      }
    })

    setFilteredData(filtered)
  }

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const handleCompanyClick = (company) => {
    setSelectedCompany(company)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedCompany(null)
  }

  const getStats = () => {
    const totalCompanies = filteredData.length
    const avgMarketCap = filteredData.reduce((sum, item) => sum + (item['Market Cap'] || 0), 0) / totalCompanies
    const avgPE = filteredData.filter(item => item['P/E']).reduce((sum, item) => sum + item['P/E'], 0) / filteredData.filter(item => item['P/E']).length
    const avgDividendYield = filteredData.filter(item => item['Dividend Yield']).reduce((sum, item) => sum + item['Dividend Yield'], 0) / filteredData.filter(item => item['Dividend Yield']).length

    return {
      totalCompanies,
      avgMarketCap,
      avgPE,
      avgDividendYield
    }
  }

  const handleAdvancedFiltering = () => {
    // Implement advanced filtering modal
    alert('Advanced filtering feature coming soon!')
  }

  const handleRealTimeData = async () => {
    // Implement real-time data updates with simulation
    try {
      setRefreshing(true)
      const simulatedData = await dataService.simulateRealTimeData()
      setData(simulatedData)
      setLastUpdated(new Date())
      setRefreshing(false)
    } catch (error) {
      console.error('Error updating real-time data:', error)
      setRefreshing(false)
    }
  }

  const handleCustomMetrics = () => {
    // Implement custom metrics
    alert('Custom metrics feature coming soon!')
  }

  const formatLastUpdated = () => {
    if (!lastUpdated) return 'Never'
    return lastUpdated.toLocaleString()
  }

  if (loading) {
    return (
      <div className="screener-loading">
        <div className="loading-spinner"></div>
        <p>Loading TSX data...</p>
      </div>
    )
  }

  const stats = getStats()

  return (
    <div className="tsx-screener">
      <div className="screener-header">
        <div className="header-title">
          <h1>TSX Screener</h1>
          <div className="header-actions">
            <button 
              onClick={refreshData} 
              className="header-action-btn refresh-btn"
              disabled={refreshing}
            >
              <RefreshCw className={refreshing ? 'spinning' : ''} size={16} />
              {refreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
            <button onClick={handleAdvancedFiltering} className="header-action-btn">
              <Search size={16} />
              Advanced Filtering
            </button>
            <button onClick={handleRealTimeData} className="header-action-btn">
              <BarChart size={16} />
              Real-time Data
            </button>
            <button onClick={handleCustomMetrics} className="header-action-btn">
              <Settings size={16} />
              Custom Metrics
            </button>
          </div>
        </div>
        
        <div className="data-status">
          <span className="last-updated">
            Last updated: {formatLastUpdated()}
          </span>
          <span className="data-source">
            Data source: Dynamic APIs + Cache
          </span>
        </div>

        <div className="header-stats">
          <div className="stat-card">
            <Database className="stat-icon" />
            <div className="stat-content">
              <h3>{stats.totalCompanies.toLocaleString()}</h3>
              <p>Companies</p>
            </div>
          </div>
          <div className="stat-card">
            <TrendingUp className="stat-icon" />
            <div className="stat-content">
              <h3>${(stats.avgMarketCap / 1000000000).toFixed(1)}B</h3>
              <p>Avg Market Cap</p>
            </div>
          </div>
          <div className="stat-card">
            <BarChart3 className="stat-icon" />
            <div className="stat-content">
              <h3>{stats.avgPE.toFixed(1)}</h3>
              <p>Avg P/E Ratio</p>
            </div>
          </div>
          <div className="stat-card">
            <Filter className="stat-icon" />
            <div className="stat-content">
              <h3>{stats.avgDividendYield.toFixed(2)}%</h3>
              <p>Avg Dividend Yield</p>
            </div>
          </div>
        </div>
      </div>

      <div className="screener-content">
        <div className="content-sidebar">
          <FilterPanel 
            filters={filters}
            data={data}
            onFilterChange={handleFilterChange}
          />
        </div>
        
        <div className="content-main">
          <DataTable 
            data={filteredData}
            onSort={handleSort}
            sortConfig={sortConfig}
            onCompanyClick={handleCompanyClick}
          />
          
          <ChartsPanel data={filteredData} />
        </div>
      </div>

      {showModal && selectedCompany && (
        <CompanyDetailModal 
          company={selectedCompany}
          onClose={closeModal}
        />
      )}
    </div>
  )
}

export default TSXScreener 